import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getIngredients, IngredientWithProductCountDto, Allergens } from '../services/ingredients';
import { PagedResult } from '../services/foods';
import { getAuthToken } from '../services/apiClient';
import { isCurrentUserAdmin } from '../services/users';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Pagination from '@mui/material/Pagination';
import Chip from '@mui/material/Chip';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';

function formatAllergens(allergens: Allergens): string {
  if (allergens === Allergens.None) return '-';
  
  const allergenNames: string[] = [];
  if (allergens & Allergens.Gluten) allergenNames.push('Lepek');
  if (allergens & Allergens.Crustaceans) allergenNames.push('Korýši');
  if (allergens & Allergens.Eggs) allergenNames.push('Vejce');
  if (allergens & Allergens.Fish) allergenNames.push('Ryby');
  if (allergens & Allergens.Peanuts) allergenNames.push('Arašídy');
  if (allergens & Allergens.Soybeans) allergenNames.push('Sója');
  if (allergens & Allergens.Milk) allergenNames.push('Mléko');
  if (allergens & Allergens.Nuts) allergenNames.push('Ořechy');
  if (allergens & Allergens.Celery) allergenNames.push('Celer');
  if (allergens & Allergens.Mustard) allergenNames.push('Hořčice');
  if (allergens & Allergens.Sesame) allergenNames.push('Sezam');
  if (allergens & Allergens.Sulphites) allergenNames.push('Siřičitany');
  if (allergens & Allergens.Lupin) allergenNames.push('Vlčí bob');
  if (allergens & Allergens.Molluscs) allergenNames.push('Měkkýši');
  
  return allergenNames.join(', ');
}

const PAGE_SIZE = 20;

export default function Ingredients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ingredientsData, setIngredientsData] = useState<PagedResult<IngredientWithProductCountDto> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [accessDenied, setAccessDenied] = useState(false);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentSearch = searchParams.get('search') || '';

  const fetchIngredients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getIngredients({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: currentSearch || undefined,
      });
      setIngredientsData(data);
    } catch (err) {
      console.error('Error fetching ingredients:', err);
      setError('Nepodařilo se načíst ingredience. Zkuste to prosím znovu.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentSearch]);

  useEffect(() => {
    if (!getAuthToken()) {
      navigate('/login');
      return;
    }

    if (!isCurrentUserAdmin()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }

    fetchIngredients();
  }, [navigate, fetchIngredients]);

  // Sync search input with URL when URL changes externally
  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchInput.trim()) {
      newParams.set('search', searchInput.trim());
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1'); // Reset to page 1 on new search
    setSearchParams(newParams);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  if (accessDenied) {
    return (
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Alert severity="error">
          Přístup odepřen. Tato stránka je pouze pro administrátory.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Ingredience
      </Typography>

      {/* Search */}
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Hledat ingredience..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        {currentSearch && (
          <Chip
            label={`Hledání: "${currentSearch}"`}
            onDelete={handleClearSearch}
            size="small"
          />
        )}
        {ingredientsData && (
          <Typography variant="body2" color="text.secondary">
            {ingredientsData.totalCount} {ingredientsData.totalCount === 1 ? 'ingredience' : ingredientsData.totalCount < 5 ? 'ingredience' : 'ingrediencí'}
            {currentSearch && ' nalezeno'}
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Název</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <InventoryIcon fontSize="small" />
                      Produkty
                    </Box>
                  </TableCell>
                  <TableCell>Alergeny</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ingredientsData?.items.map((item) => (
                  <TableRow key={item.ingredient.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {item.ingredient.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.productCount}
                        size="small"
                        color={item.productCount > 0 ? 'primary' : 'default'}
                        variant={item.productCount > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatAllergens(item.ingredient.allergens)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {ingredientsData && ingredientsData.items.length === 0 && (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
              {currentSearch ? `Žádné ingredience neodpovídají hledání "${currentSearch}".` : 'Žádné ingredience nenalezeny.'}
            </Typography>
          )}

          {/* Pagination */}
          {ingredientsData && ingredientsData.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={ingredientsData.totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

